import { Navigate, useLocation, useParams } from 'react-router-dom'
import { Spinner, Typography } from '@material-tailwind/react'
import { useTranslation } from 'react-i18next'

import AgamiFormV2 from './AgamiFormV2'
import GoBack from '../components/GoBack'
import { useSessionContext } from '../contexts/SessionContext'
import { usePageTitle } from '../hooks/toolkit'
import { useFeatureFlag } from '../hooks/authorization'
import { FEATURE_FLAGS } from '../utils/constants/features.constants'
import { useAgami } from '../hooks/useAgami'

/**
 * Edit page of the v2 form (06.1), the counterpart of DashboardAgamiDetail on
 * `/spot/v2/:id`. The flag guard is the route target's, like on the creation
 * page: without the flag one lands on the v1 detail of the same agami, and
 * neither form knows the other exists.
 */
const AgamiDetailV2: React.FC = () => {
  const { t } = useTranslation()
  usePageTitle(t('dashboardAgamiDetail.pageTitle'))

  const { id } = useParams()
  const location = useLocation()
  const { loading } = useSessionContext()
  const ondeFlag = useFeatureFlag(FEATURE_FLAGS.ONDE)

  const { agami, isLoading: agamiLoading, refetch } = useAgami({ agamiId: id || '' })

  if (!loading && !ondeFlag) {
    return (
      <Navigate
        to={{ pathname: `/spot/${id}`, search: location.search, hash: location.hash }}
        state={location.state}
        replace
      />
    )
  }

  const isLoading = loading || agamiLoading

  return (
    <div className="w-full flex flex-col py-4">
      <div className="flex items-center justify-center gap-2 self-start">
        <GoBack />
        <Typography variant="lead" className="text-primary" data-testid="agami-detail-v2-title">
          {agami?.name ?? ''}
        </Typography>
      </div>

      {isLoading && (
        <div className="w-full my-10">
          <Spinner className="h-12 w-12 mx-auto" />
        </div>
      )}

      {!isLoading && !agami && (
        <div className="w-full my-10">
          <Typography>{t('dashboardAgamiDetail.notFound')}</Typography>
        </div>
      )}

      {!isLoading && agami && <AgamiFormV2 agami={agami} mode="author" onSaved={() => refetch()} />}
    </div>
  )
}

export default AgamiDetailV2
